<template>
  <div class="flex flex-col">
    <input
      v-if="isActive"
      v-model="inputPlaceholder"
      class="form-control"
      :class="classList"
      :style="styleObject"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <input
      v-else
      class="form-control"
      :class="classList"
      :style="styleObject"
      :placeholder="placeholder"
      :readonly="readonly"
    />
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleInputShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">{{
        element.shortLabel
      }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import {
  fixSafariCursor,
  handleShortLabelInput as handleShortLabelInputUtil,
} from "@/util/methods";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
const props = defineProps({
  element: {
    type: Object,
    default: () => {
      return {};
    },
  },
  placeholder: { type: String, default: "" },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  styleObject: {
    type: Object,
    default: () => {
      return {
        fontSize: "14px",
      };
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const shortLabel = ref(props.element.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const store = useAppStore();

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const handleInput = () => {
  emit("update:placeholder", inputPlaceholder.value);
};

const handleFocus = () => {
  if (!inputPlaceholder.value) {
    inputPlaceholder.value = props.placeholder;
  }
};

const handleBlur = () => {
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style scoped>
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}
</style>
